import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Ban, CheckCircle } from "lucide-react";

export default function ActivateAccountPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    api.get(`/auth/activation/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(fmtErr(e, "Lien invalide")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSaving(true);
    try {
      await api.post("/auth/activate", { token, password, password_confirm: confirm });
      setDone(true);
    } catch (err) {
      setSubmitError(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const loginUrl = info?.portal_slug ? `/${info.portal_slug}` : "/login";
  const valid = password.length >= 8 && password === confirm;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6" data-testid="activate-page">
      <div className="w-full max-w-md rounded-sm border border-border bg-card p-8 sm:p-10">
        <div className="font-heading text-xl font-semibold">Suivio<span className="text-primary">360</span></div>

        {error && (
          <div className="mt-8 text-center" data-testid="activate-error">
            <Ban className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-6 text-2xl font-semibold">Lien invalide ou expiré</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{error}</p>
            <p className="mt-2 text-sm text-muted-foreground">Demandez un nouveau lien d'activation à votre administrateur.</p>
          </div>
        )}

        {done && (
          <div className="mt-8 text-center" data-testid="activate-success">
            <CheckCircle className="mx-auto h-10 w-10 text-green-600" />
            <h1 className="mt-6 text-2xl font-semibold">Compte activé</h1>
            <p className="mt-3 text-sm text-muted-foreground">Votre mot de passe est défini. Vous pouvez maintenant vous connecter.</p>
            <Link to={loginUrl} data-testid="activate-login-link"
              className="mt-6 inline-block rounded-sm bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors">
              Se connecter
            </Link>
          </div>
        )}

        {info && !done && !error && (
          <>
            <h1 className="mt-8 text-2xl font-semibold tracking-tight">Activez votre compte</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Bonjour {info.first_name}, définissez votre mot de passe pour <b>{info.email}</b>.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label>Mot de passe</Label>
                <div className="relative">
                  <Input data-testid="activate-password" type={show ? "text" : "password"} required value={password}
                    onChange={(e) => setPassword(e.target.value)} className="rounded-sm pr-9" placeholder="8 caractères minimum" />
                  <button type="button" data-testid="activate-toggle-password" onClick={() => setShow(!show)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirmer le mot de passe</Label>
                <Input data-testid="activate-password-confirm" type={show ? "text" : "password"} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className="rounded-sm" />
              </div>
              {confirm && password !== confirm && <p data-testid="activate-mismatch" className="text-xs font-medium text-primary">Les mots de passe ne correspondent pas.</p>}
              {submitError && <div data-testid="activate-submit-error" className="rounded-sm border border-primary/40 bg-[hsl(var(--note-bg))] px-4 py-3 text-sm text-primary">{submitError}</div>}
              <Button data-testid="activate-submit" type="submit" disabled={saving || !valid}
                className="w-full rounded-sm bg-primary text-white hover:bg-red-700 transition-colors duration-200">
                {saving ? "Activation…" : "Activer mon compte"}
              </Button>
            </form>
          </>
        )}

        {!info && !error && <div className="mt-8 text-sm text-muted-foreground">Vérification du lien…</div>}
      </div>
    </div>
  );
}
